import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { mobilizeiSubscriptionsService } from '../../../services/mobilizei/subscriptions.service';
import BillingLayout from '../../../layouts/billing';
import Step1 from './Step1';
import Step2 from './Step2';
import CreditCardStep from './CreditCardStep';
import BankSlipData from '../../../components/billing/BankSlipData';
import PixData from '../../../components/billing/PixData';
import AutomaticPixData from '../../../components/billing/AutomaticPixData';
import { toast } from "react-toastify";
import { usePlan } from '../../../contexts/plan.context';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useUser } from '../../../contexts/user.context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { automaticPixStatus, paymentMethods, plans, storeStatus } from '../../../utils/constants';
import { useForm } from 'react-hook-form';
import useSubscriptionActivation from '../../../hooks/subscriptionActivation.hook';

export default function Subscription() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [subscription, setSubscription] = useState({});
    const { plan, setPlan } = usePlan();
    const { user } = useUser();
    const [searchParams] = useSearchParams();

    // The checkout usually gets the plan from the pricing page, but it can also be opened
    // with a plan on the URL (e.g. /hire?planId=plano_basico_mensal_2024)
    const planIdOnUrl = searchParams.get('planId');
    useEffect(() => {
        if (planIdOnUrl && plans[planIdOnUrl] && planIdOnUrl !== plan) {
            setPlan(planIdOnUrl);
        }
    }, [planIdOnUrl]);

    useEffect(() => {
        if (user?.store?.status === storeStatus.ACTIVE) {
            navigate('/')
            return;
        }
    }, [user]);

    const { control, getValues, handleSubmit, setValue, watch, register, formState: { errors } } = useForm({
        defaultValues: {
            cpfCnpj: '',
            email: '',
            name: '',
            phone: '',
            paymentMethod: 'credit_card',
            planId: plan,
            zipCode: '',
            number: '',
            street: '',
            city: '',
            state: '',
            district: '',
            complement: '',
            creditCardToken: '',
            installments: 1,
            creditCard: {
                number: '',
                cvv: '',
                name: '',
                expiration: ''
            },
        }
    });

    // The planId that is sent to the API is a form value, and the form only reads it from the
    // context on its first render. The plan can change after that (e.g. the planId on the URL),
    // so we keep the form in sync with the context.
    useEffect(() => {
        setValue('planId', plan);
    }, [plan]);

    const watchEmail = watch('email', '');
    const watchPaymentMethod = watch('paymentMethod', 'credit_card');

    // Pix payments are confirmed asynchronously, so we poll the subscription
    // to show the success screen as soon as the payment is confirmed
    const isPixPayment = [paymentMethods.PIX, paymentMethods.AUTOMATIC_PIX].includes(watchPaymentMethod);
    const { subscription: activeSubscription, isActive, hasTimedOut } = useSubscriptionActivation(
        subscription?.id,
        currentStep === 3 && isPixPayment
    );

    const PaymentConfirmationStatus = () => (
        hasTimedOut ? (
            <Typography variant="body2" align="center" color="textSecondary">
                Ainda não identificamos o seu pagamento. Assim que ele for confirmado, te avisaremos pelo e-mail <strong>{watchEmail}</strong>.
            </Typography>
        ) : (
            <Box alignItems="center" display="flex" justifyContent="center">
                <CircularProgress size={18} sx={{ mr: 1.5 }} />
                <Typography variant="body2" color="textSecondary">
                    Aguardando a confirmação do pagamento...
                </Typography>
            </Box>
        )
    )

    const getAutomaticPixSuccessNotice = () => {
        switch (activeSubscription?.automaticPix?.status) {
            case automaticPixStatus.AUTHORIZED:
                return <Alert severity="success">Pix Automático ativado! As próximas renovações serão debitadas automaticamente da sua conta.</Alert>
            case automaticPixStatus.REJECTED:
            case automaticPixStatus.EXPIRED:
            case automaticPixStatus.CANCELLED:
                return <Alert severity="warning">O pagamento foi confirmado, mas o Pix Automático não foi autorizado. Na próxima renovação você receberá por e-mail um novo QR Code para pagar e autorizar o débito automático.</Alert>
            default:
                return <Alert severity="info">Se você autorizou o Pix Automático no app do seu banco, a confirmação pode levar alguns minutos. Você pode acompanhar o status na área "Minha conta".</Alert>
        }
    }

    const changeStep = step => {
        setCurrentStep(step)
        window.scrollTo(0, 0);
    }

    async function onSubmit(creditCardToken, recaptchaData) {
        setLoading(true);

        // Crete a 'data' object without the creditCard attribute (so we don't POST the credit card data)
        const { creditCard, ...data } = getValues();

        await mobilizeiSubscriptionsService.create({ ...data, creditCardToken, ...recaptchaData })
            .then(response => {
                setSubscription(response);
                changeStep(3);
            })
            .catch(error => {
                let message = 'Não foi possível realizar a ativação da sua conta. Por favor, confira os seus dados e tente novamente.';

                const errorCode = _.get(error, 'error.code', '');

                if (errorCode === '2142') {
                    message = _.get(error, 'error.message', message);
                    toast.error(
                        <div>
                            Não foi possível processar o pagamento. <br /><br />
                            {message} <br /><br />
                            Por favor, confira os seus dados e tente novamente.
                        </div>
                    );
                } else {
                    toast.error(`${message} (#${errorCode})`)
                }
            })
            .finally(() => {
                setLoading(false);
            })
    }

    return (
        <BillingLayout fullScreen>
            <Box paddingY={2}>
                {currentStep === 1 &&
                    <Step1
                        control={control}
                        watch={watch}
                        register={register}
                        errors={errors}
                        setValue={setValue}
                        goToStep2={handleSubmit(() => changeStep(2))}
                    />
                }

                {currentStep === 2 &&
                    <Step2
                        watch={watch}
                        goToPreviousStep={() => changeStep(1)}
                        goToNextStep={onSubmit}
                        loading={loading}
                        setValue={setValue}
                        setLoading={setLoading}
                        register={register}
                    />
                }

                {currentStep === 3 && watchPaymentMethod === 'credit_card' &&
                    <CreditCardStep watch={watch} subscription={subscription} />
                }

                {currentStep === 3 && watchPaymentMethod === 'bank_slip' &&
                    <BankSlipData
                        title="Pagamento via boleto"
                        text={<>O acesso à sua conta será liberado após a compensação do boleto, em até 3 dias úteis. Te manteremos informado a respeito do status do pagamento através do e-mail <strong>{watchEmail}</strong>.</>}
                        disclaimer="Próximo da data de vencimento você irá receber no seu e-mail um link para efetuar a renovação da sua assinatura. Utilizando este link, você poderá efetuar um novo pagamento via boleto ou então utilizar algum outro método de pagamento."
                        invoice={_.get(subscription, 'invoice', {})}
                    />
                }

                {currentStep === 3 && isPixPayment && isActive &&
                    <CreditCardStep notice={watchPaymentMethod === paymentMethods.AUTOMATIC_PIX && getAutomaticPixSuccessNotice()} />
                }

                {currentStep === 3 && watchPaymentMethod === paymentMethods.PIX && !isActive &&
                    <PixData
                        title="Pagamento Pix"
                        text={<>Efetue o pagamento utilizando os dados abaixo. Te avisaremos pelo e-mail <strong>{watchEmail}</strong> assim que o acesso à sua conta for liberado.</>}
                        disclaimer="Próximo da data de vencimento você irá receber no seu e-mail um link para efetuar a renovação da sua assinatura. Utilizando este link, você poderá efetuar um novo pagamento via Pix ou então utilizar algum outro método de pagamento."
                        invoice={_.get(subscription, 'invoice', {})}
                    >
                        <PaymentConfirmationStatus />
                    </PixData>
                }

                {currentStep === 3 && watchPaymentMethod === paymentMethods.AUTOMATIC_PIX && !isActive &&
                    <AutomaticPixData
                        title="Pagamento com Pix Automático"
                        text={<>Pague a primeira cobrança e autorize o Pix Automático no app do seu banco. Esta página será atualizada assim que o pagamento for confirmado.</>}
                        disclaimer="Nas próximas renovações, o valor da sua assinatura será debitado automaticamente da sua conta, na data de vencimento. Você pode cancelar o Pix Automático a qualquer momento pelo painel da Mobilizei ou pelo app do seu banco."
                        invoice={_.get(subscription, 'invoice', {})}
                    >
                        <PaymentConfirmationStatus />
                    </AutomaticPixData>
                }
            </Box>
        </BillingLayout>
    );
}